import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Home, Mail, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const ThankYou = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Scroll to top on component mount
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Thank You - Registration Successful | Trademark Frontline</title>
        <meta name="description" content="Thank you for contacting Trademark Frontline. Our team will reach out to you shortly to assist with your trademark registration." />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-[#4B0082]/5 to-[#FF7F7F]/10 flex items-center justify-center px-4 py-16">
        <Card className="max-w-2xl w-full shadow-2xl">
          <CardContent className="p-8 md:p-12 text-center">
            {/* Success Icon */}
            <div className="mb-6 flex justify-center">
              <div className="bg-green-100 rounded-full p-4">
                <CheckCircle2 className="h-16 w-16 text-green-600" />
              </div>
            </div>

            {/* Main Heading */}
            <h1 className="text-3xl md:text-4xl font-bold text-[#4B0082] mb-4">
              Thank You!
            </h1>
            
            <p className="text-xl text-gray-700 mb-6">
              Your request has been submitted successfully.
            </p>

            {/* Success Message */}
            <div className="bg-[#FF7F7F]/10 border border-[#FF7F7F]/20 rounded-lg p-6 mb-8">
              <p className="text-gray-800 leading-relaxed">
                We've received your information and our trademark specialists will contact you shortly. 
                We'll help you protect your brand with our professional trademark registration services.
              </p>
            </div>

            {/* What's Next Section */}
            <div className="mb-8 text-left bg-white border border-gray-200 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-[#4B0082] mb-4 text-center">
                What Happens Next?
              </h2>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-[#FF7F7F] mt-0.5 flex-shrink-0" />
                  <span>Our team will review your submission within 24 hours</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-[#FF7F7F] mt-0.5 flex-shrink-0" />
                  <span>A trademark specialist will contact you via phone or email</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-[#FF7F7F] mt-0.5 flex-shrink-0" />
                  <span>We'll guide you through the entire trademark registration process</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-[#FF7F7F] mt-0.5 flex-shrink-0" />
                  <span>Get started with protecting your brand in just a few simple steps</span>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div className="mb-8 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-3">Need immediate assistance?</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
                <a 
                  href="tel:+18446270636" 
                  className="flex items-center gap-2 text-[#4B0082] hover:text-[#FF7F7F] transition-colors"
                >
                  <Phone className="h-4 w-4" />
                  <span>+1 (844) 627 0636</span>
                </a>
                <span className="hidden sm:inline text-gray-300">|</span>
                <a 
                  href="mailto:info@trademarkfrontline.com" 
                  className="flex items-center gap-2 text-[#4B0082] hover:text-[#FF7F7F] transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  <span>info@trademarkfrontline.com</span>
                </a>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/')}
                size="lg"
                className="bg-[#4B0082] hover:bg-[#3a0062] text-white"
              >
                <Home className="mr-2 h-5 w-5" />
                Return to Home
              </Button>
              <Button
                onClick={() => navigate('/contact')}
                size="lg"
                variant="outline"
                className="border-[#4B0082] text-[#4B0082] hover:bg-[#4B0082] hover:text-white"
              >
                Contact Us Again
              </Button>
            </div>

            {/* Additional Note */}
            <p className="text-xs text-gray-500 mt-8">
              If you don't hear from us within 24 hours, please check your spam folder or contact us directly.
            </p>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default ThankYou;
